/* Данные экрана студии без React (engine-kit, план M3 §2 S4): GET /app/studio/screens/<screen> или свой загрузчик — правила
   useStudioDocument (use-studio-document.ts), только для движка: ответ принадлежит владельцу, для которого его спросили (сменился
   владелец — прежние данные не видны, опоздавший ответ никуда не ложится); remember — ответ переживает уход из раздела (память
   studio-store по владельцу, как у родной формы): открытие рисуется из памяти сразу, запрос подтверждает её в фоне, сбой подтверждения
   её не снимает; в память ложится только ответ того же владельца и языка, при которых его спросили. reload() — «Повторить» (новый
   запрос, до ответа данных нет — как reload родного хука); abort() — уход (запрос прерывается). onChange — пришёл ответ или сбой. */
import { api } from '../../../api';
import { accountLocale } from '../../../i18n';
import { recallStudioDocument, rememberStudioDocument, studioOwner } from '../studio-store';

export interface ScreenDoc<T> {
  /** Данные текущего владельца: из ответа или из памяти раздела */
  data(): T | undefined;
  /** Загрузка не удалась, а данных нет */
  error(): boolean;
  /** Ждём ответа, а данных нет */
  loading(): boolean;
  /** Загрузить, если для текущего владельца ещё не загружено; идущий запрос того же владельца — тот же промис. Не бросает: сбой — undefined */
  load(): Promise<T | undefined>;
  /** «Повторить»: прежний запрос прерывается, данные сбрасываются, идёт новый */
  reload(): Promise<T | undefined>;
  /** Уход: идущий запрос прерывается, его ответ уже никуда не ляжет */
  abort(): void;
}
export interface ScreenDocOptions<T> {
  /** Ключ памяти раздела (как identity useStudioDocument) */
  identity: string;
  load(signal: AbortSignal): Promise<T>;
  remember?: boolean;
  onChange?(): void;
}

interface Held<T> {
  owner: string;
  data?: T;
  /** Данные пришли ответом этого объекта (из памяти — false: их ещё подтверждает запрос) */
  confirmed: boolean;
  error: boolean;
  flight: { abort: AbortController; done: Promise<T | undefined> } | null;
}

export function createScreenDoc<T>(o: ScreenDocOptions<T>): ScreenDoc<T> {
  let held: Held<T> = fresh();
  function fresh(withMemory = true): Held<T> {
    const owner = studioOwner();
    const kept = withMemory && o.remember ? recallStudioDocument<T>(o.identity) : undefined;
    return { owner, data: kept, confirmed: false, error: false, flight: null };
  }
  /** Состояние текущего владельца: сменился — прежнее уходит целиком (идущий запрос прерывается) */
  function current(): Held<T> {
    if (held.owner !== studioOwner()) {
      held.flight?.abort.abort();
      held = fresh();
    }
    return held;
  }
  const changed = () => o.onChange?.();
  function start(at: Held<T>): Promise<T | undefined> {
    const abort = new AbortController();
    const locale = accountLocale();
    const done = o
      .load(abort.signal)
      .then(
        data => {
          if (abort.signal.aborted || held !== at || at.owner !== studioOwner()) return undefined;
          at.data = data;
          at.confirmed = true;
          at.error = false;
          if (o.remember && locale === accountLocale()) rememberStudioDocument(o.identity, data);
          return data;
        },
        () => {
          if (abort.signal.aborted || held !== at) return undefined;
          /* данные из памяти остаются на месте, если подтверждение не пришло: сбой — только без данных */
          if (at.data === undefined) at.error = true;
          return at.data;
        },
      )
      .finally(() => {
        if (at.flight?.abort === abort) at.flight = null;
        if (!abort.signal.aborted && held === at) changed();
      });
    at.flight = { abort, done };
    return done;
  }
  return {
    data: () => current().data,
    error: () => current().error,
    loading: () => {
      const at = current();
      return at.data === undefined && !at.error;
    },
    load() {
      const at = current();
      if (at.flight) return at.flight.done;
      if (at.confirmed || at.error) return Promise.resolve(at.data);
      return start(at);
    },
    reload() {
      current().flight?.abort.abort();
      held = fresh(false);
      return start(held);
    },
    abort() {
      const at = held;
      at.flight?.abort.abort();
      at.flight = null;
    },
  };
}

/** Документ экрана GET /app/studio/screens/<screen> (ключ памяти по умолчанию — '<screen>-screen') */
export function screenDoc<T = Record<string, unknown>>(screen: string, o: { identity?: string; remember?: boolean; onChange?(): void } = {}): ScreenDoc<T> {
  return createScreenDoc<T>({
    identity: o.identity ?? `${screen}-screen`,
    load: signal => api.request<T>(`/app/studio/screens/${encodeURIComponent(screen)}`, { signal }),
    remember: o.remember,
    onChange: o.onChange,
  });
}
